type Input = { [key: string]: any };
type CheckedProperty = "checked" | "isChecked" | "isSelected";

export function updateInputs(
  inputs: Input[],
  targetId: string | undefined = "",
  checkedProperty: CheckedProperty = "checked"
) {
  const { parentIds, inputsMap } = createInputsMap(inputs);
  const targetInput = inputsMap.get(targetId);

  targetInput[checkedProperty] = !targetInput[checkedProperty];
  targetInput.isIndeterminate = false;

  updateInputChildren(targetInput, checkedProperty);
  updateInputParents(parentIds, inputsMap, targetInput, checkedProperty);
}

function createInputsMap(inputs: Input[]) {
  const parentIds = new Map();
  const inputsMap = new Map();

  function recursiveAdd(input: Input, parentId?: string) {
    parentIds.set(input.id, parentId);
    inputsMap.set(input.id, input);

    if (input.children) {
      input.children.forEach((nestedInput: Input) => {
        recursiveAdd(nestedInput, input.id);
      });
    }
  }

  inputs.forEach((input: Input) => {
    recursiveAdd(input);
  });

  return { parentIds, inputsMap };
}

function updateInputChildren(
  targetInput: Input,
  checkedProperty: CheckedProperty
) {
  function recursiveUpdate(input: Input) {
    input[checkedProperty] = targetInput[checkedProperty];
    input.isIndeterminate = false;

    if (input.children) {
      input.children.forEach((nestedInput: Input) => {
        recursiveUpdate(nestedInput);
      });
    }
  }

  recursiveUpdate(targetInput);
}

function updateInputParents(
  parentIds: Map<string, string>,
  inputsMap: Map<string, Input>,
  targetInput: Input,
  checkedProperty: CheckedProperty
) {
  function recursiveUpdate(input: Input) {
    const parentId = parentIds.get(input.id);
    const parentInput = inputsMap.get(parentId || "");

    if (parentInput && parentInput.children) {
      const { children } = parentInput;

      const checkedChildren = children.filter((childInput: Input) => {
        return childInput[checkedProperty];
      });

      const indeterminateChildren = children.filter(
        ({ isIndeterminate }: Input) => {
          return isIndeterminate;
        }
      );

      const someIndeterminateChildren = indeterminateChildren.length > 0;
      const noChildrenChecked = checkedChildren.length === 0;
      const allChildrenChecked = checkedChildren.length === children.length;

      if (someIndeterminateChildren) {
        parentInput[checkedProperty] = false;
        parentInput.isIndeterminate = true;
      } else if (noChildrenChecked) {
        parentInput[checkedProperty] = false;
        parentInput.isIndeterminate = false;
      } else if (allChildrenChecked) {
        parentInput[checkedProperty] = true;
        parentInput.isIndeterminate = false;
      } else {
        parentInput[checkedProperty] = false;
        parentInput.isIndeterminate = true;
      }

      recursiveUpdate(parentInput);
    }
  }

  recursiveUpdate(targetInput);
}
